import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, Filter, Star, X, Dices, Gamepad2, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import CasinoCard from "../components/CasinoCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gameTypes = ["Slots", "Blackjack", "Roulette", "Poker", "Live Casino", "Crash Games", "Baccarat"];
const paymentMethods = ["Visa", "Mastercard", "PayPal", "Skrill", "Neteller", "Bitcoin", "Crypto"];

export default function Casinos() {
  const [casinos, setCasinos] = useState([]);
  const [filteredCasinos, setFilteredCasinos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [filters, setFilters] = useState({
    minRating: null,
    gameType: null,
    paymentMethod: null,
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchCasinos = async () => {
      try {
        const response = await axios.get(`${API}/casinos`);
        setCasinos(response.data);
        setFilteredCasinos(response.data);
        if (searchParams.get("q")) setSearchQuery(searchParams.get("q"));
      } catch (error) {
        console.error("Error fetching casinos:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCasinos();
  }, []);

  useEffect(() => {
    let result = casinos;

    if (searchQuery) {
      result = result.filter(casino => 
        casino.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        casino.bonus_description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filters.minRating) {
      result = result.filter(casino => casino.rating >= parseFloat(filters.minRating));
    }

    if (filters.gameType) {
      result = result.filter(casino => 
        casino.games.some(g => g.toLowerCase().includes(filters.gameType.toLowerCase()))
      );
    }

    if (filters.paymentMethod) {
      result = result.filter(casino => 
        casino.payment_methods.some(p => p.toLowerCase().includes(filters.paymentMethod.toLowerCase()))
      );
    }

    setFilteredCasinos(result);
  }, [searchQuery, filters, casinos]);

  const clearFilters = () => {
    setFilters({ minRating: null, gameType: null, paymentMethod: null });
    setSearchQuery("");
  };

  const hasActiveFilters = filters.minRating || filters.gameType || filters.paymentMethod || searchQuery;

  return (
    <div className="min-h-screen py-6" data-testid="casinos-page">
      <Helmet>
        <title>Best Online Casinos, Slots & Crash Games 2026 – PlaySpiro</title>
        <meta name="description" content="Compare 100+ online casinos, slot sites and crash games. Filter by rating, game type and payment method. Find exclusive bonuses at PlaySpiro." />
        <link rel="canonical" href="https://playspiro.com/casinos" />
        <meta property="og:title" content="Best Online Casinos, Slots & Crash Games 2026 – PlaySpiro" />
        <meta property="og:description" content="Compare 100+ online casinos, slot sites and crash games with exclusive bonuses." />
        <meta property="og:url" content="https://playspiro.com/casinos" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* Header */}
      <section className="gradient-hero py-10 mb-6">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 font-['Montserrat']">
            Casino, Slots & Crash <span className="text-primary">Reviews</span>
          </h1>
          <p className="text-white/60 max-w-xl mx-auto text-sm">
            Find the best online casinos, slot games, and crash gambling sites with exclusive bonuses.
          </p>
          
          {/* Category Pills */}
          <div className="flex justify-center gap-3 mt-5">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary text-sm">
              <Dices className="w-4 h-4" /> Casino
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/20 text-secondary text-sm">
              <Gamepad2 className="w-4 h-4" /> Slots
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent text-sm">
              <Zap className="w-4 h-4" /> Crash
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4">
        {/* Search & Filter Bar */}
        <div className="glass rounded-xl p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search casinos, slots, crash games..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10 bg-muted border-border text-white placeholder:text-muted-foreground text-sm"
                data-testid="casino-search-input"
              />
            </div>

            {/* Filter Toggle (Mobile) */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden border-border text-white h-10"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>

            {/* Desktop Filters */}
            <div className="hidden md:flex items-center gap-2">
              <Select
                value={filters.minRating || ""}
                onValueChange={(value) => setFilters(prev => ({ ...prev, minRating: value || null }))}
              >
                <SelectTrigger className="w-32 bg-muted border-border text-white h-10 text-sm" data-testid="rating-filter">
                  <SelectValue placeholder="Rating" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4.5">4.5+ Stars</SelectItem>
                  <SelectItem value="4">4+ Stars</SelectItem>
                  <SelectItem value="3.5">3.5+ Stars</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.gameType || ""}
                onValueChange={(value) => setFilters(prev => ({ ...prev, gameType: value || null }))}
              >
                <SelectTrigger className="w-36 bg-muted border-border text-white h-10 text-sm" data-testid="game-filter">
                  <SelectValue placeholder="Game Type" />
                </SelectTrigger>
                <SelectContent>
                  {gameTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.paymentMethod || ""}
                onValueChange={(value) => setFilters(prev => ({ ...prev, paymentMethod: value || null }))}
              >
                <SelectTrigger className="w-36 bg-muted border-border text-white h-10 text-sm" data-testid="payment-filter">
                  <SelectValue placeholder="Payment" />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map(method => (
                    <SelectItem key={method} value={method}>{method}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-muted-foreground hover:text-white h-10"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
            </div>
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <div className="md:hidden mt-3 pt-3 border-t border-border space-y-2">
              <Select
                value={filters.minRating || ""}
                onValueChange={(value) => setFilters(prev => ({ ...prev, minRating: value || null }))}
              >
                <SelectTrigger className="w-full bg-muted border-border text-white h-10">
                  <SelectValue placeholder="Minimum Rating" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4.5">4.5+ Stars</SelectItem>
                  <SelectItem value="4">4+ Stars</SelectItem>
                  <SelectItem value="3.5">3.5+ Stars</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.gameType || ""}
                onValueChange={(value) => setFilters(prev => ({ ...prev, gameType: value || null }))}
              >
                <SelectTrigger className="w-full bg-muted border-border text-white h-10">
                  <SelectValue placeholder="Game Type" />
                </SelectTrigger>
                <SelectContent>
                  {gameTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="w-full border-border text-white h-10"
                >
                  <X className="w-4 h-4 mr-1" /> Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-muted-foreground text-sm">
            Showing <span className="text-white font-medium">{filteredCasinos.length}</span> results
          </p>
        </div>

        {/* Ad Banner */}
        <div className="ad-banner h-[90px] rounded-lg mb-6" data-testid="ad-inline">
          <span>Advertisement</span>
        </div>

        {/* Casino Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="h-64 rounded-2xl bg-card animate-pulse" />
            ))}
          </div>
        ) : filteredCasinos.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredCasinos.map((casino, index) => (
              <CasinoCard key={casino.id} casino={casino} featured={index === 0} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">No results found.</p>
            <Button onClick={clearFilters} className="gradient-neon text-white rounded-full">
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
